import { roleHasPermission } from "@bafe/types";
import { Select } from "@bafe/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";

import { api } from "../../lib/api.js";
import { presentError } from "../../lib/errors.js";
import { queryKeys } from "../../lib/query.js";
import { activeMembership, useSession } from "../../lib/session.js";

type LeavePolicy = { kind: string; days: number; paid: boolean; approval: boolean };
type ClaimPolicy = { kind: string; limit: string; approval: boolean };
type Shift = { id?: string; name: string; start: string; end: string };
type ShiftWithId = { id: string; name: string; start: string; end: string };
type AttendanceSlot = { id?: number; person: string; days: string[]; shift: string; mine?: boolean };

export type PeopleSettingsBooks = {
	leavePolicy: LeavePolicy[];
	claimPolicy: ClaimPolicy[];
	attendanceSettings: { lateMinutes: number; shifts: Shift[] };
	payrollSettings: { payDay: number; epf: number; socso: string; eis: string };
	attendanceSlots: AttendanceSlot[];
};

const WEEKDAYS: Array<[string, string]> = [
	["mon", "Mon"],
	["tue", "Tue"],
	["wed", "Wed"],
	["thu", "Thu"],
	["fri", "Fri"],
	["sat", "Sat"],
	["sun", "Sun"],
];

const TAB_ITEMS: Array<[string, string]> = [
	["leave", "Leave"],
	["attendance", "Attendance"],
	["claims", "Claims"],
	["payroll", "Payroll"],
];

const LABEL: Record<string, string> = {
	annual: "Annual",
	medical: "Medical",
	fuel: "Fuel",
	meals: "Meals",
	parking: "Parking",
	travel: "Travel",
	tools: "Tools",
};

function label(value: string): string {
	return LABEL[value] ?? (value ? value.charAt(0).toUpperCase() + value.slice(1) : "");
}

function slugKind(value: string): string {
	return value
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 40);
}

function shiftId(name: string, existing: Shift[]): string {
	const base = slugKind(name) || "shift";
	if (!existing.some((row) => (row.id ?? slugKind(row.name)) === base)) return base;
	let n = 2;
	while (existing.some((row) => (row.id ?? slugKind(row.name)) === `${base}-${n}`)) n += 1;
	return `${base}-${n}`;
}

function shiftsWithIds(rows: Shift[]): ShiftWithId[] {
	return rows.map((row) => ({
		id: row.id ?? shiftId(row.name, rows),
		name: row.name,
		start: row.start,
		end: row.end,
	}));
}

function useCanManageHub() {
	const session = useSession();
	const organisationId = session.data?.activeOrganisationId ?? session.data?.memberships[0]?.organisationId ?? null;
	return useMemo(() => {
		const membership = session.data ? activeMembership(session.data, organisationId) : null;
		return membership ? roleHasPermission(membership.role, "hub:manage") : false;
	}, [organisationId, session.data]);
}

function SectionBar({ sub, children }: { sub: string; children?: React.ReactNode }) {
	return (
		<div className="row">
			<p className="sub">{sub}</p>
			{children ? <div className="actions">{children}</div> : null}
		</div>
	);
}

function SettingsTabs({ current, onPick }: { current: string; onPick: (value: string) => void }) {
	return (
		<div className="filters">
			{TAB_ITEMS.map(([value, text]) => (
				<button key={value} type="button" className={current === value ? "chip active" : "chip"} onClick={() => onPick(value)}>
					{text}
				</button>
			))}
		</div>
	);
}

function ToggleRow({ label, checked, disabled, onChange }: { label: string; checked: boolean; disabled?: boolean; onChange: (next: boolean) => void }) {
	return (
		<label className="row gap">
			<input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
			<span>{label}</span>
		</label>
	);
}

export function PeopleSettingsHub({ books }: { books: PeopleSettingsBooks }) {
	const client = useQueryClient();
	const canManage = useCanManageHub();
	const [params, setParams] = useSearchParams();
	const tab = params.get("tab") ?? "leave";
	const pickTab = (value: string) => {
		const next = new URLSearchParams(params);
		next.set("tab", value);
		setParams(next);
	};

	const [leavePolicy, setLeavePolicy] = useState<LeavePolicy[]>(() => [...books.leavePolicy]);
	const [claimPolicy, setClaimPolicy] = useState<ClaimPolicy[]>(() => [...books.claimPolicy]);
	const [lateMinutes, setLateMinutes] = useState(() => books.attendanceSettings.lateMinutes || 0);
	const [shifts, setShifts] = useState<ShiftWithId[]>(() => shiftsWithIds([...books.attendanceSettings.shifts]));
	const [attendanceSlots, setAttendanceSlots] = useState<AttendanceSlot[]>(() => [...books.attendanceSlots]);
	const [payDay, setPayDay] = useState(() => books.payrollSettings.payDay || 0);
	const [epf, setEpf] = useState(() => books.payrollSettings.epf || 0);
	const [socso, setSocso] = useState(() => books.payrollSettings.socso || "");
	const [eis, setEis] = useState(() => books.payrollSettings.eis || "");

	const [error, setError] = useState("");
	const [pending, setPending] = useState(false);
	const [saved, setSaved] = useState("");

	useEffect(() => {
		setLeavePolicy([...books.leavePolicy]);
		setClaimPolicy([...books.claimPolicy]);
		setLateMinutes(books.attendanceSettings.lateMinutes || 0);
		setShifts(shiftsWithIds([...books.attendanceSettings.shifts]));
		setAttendanceSlots([...books.attendanceSlots]);
		setPayDay(books.payrollSettings.payDay || 0);
		setEpf(books.payrollSettings.epf || 0);
		setSocso(books.payrollSettings.socso || "");
		setEis(books.payrollSettings.eis || "");
	}, [books]);

	async function save(patch: Parameters<typeof api.updateHubPeopleSettings>[0]) {
		if (!canManage) return;
		setPending(true);
		setError("");
		setSaved("");
		try {
			await api.updateHubPeopleSettings(patch);
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
			setSaved("Settings saved.");
		} catch (err) {
			setError(presentError(err).title);
		} finally {
			setPending(false);
		}
	}

	return (
		<div>
			<SectionBar sub="Configure leave, attendance, claims and payroll in one place.">
				{canManage ? null : <span className="quiet">View only. Hub manage access is required to change settings.</span>}
			</SectionBar>
			<SettingsTabs current={tab} onPick={pickTab} />
			{error ? <p className="err">{error}</p> : null}
			{saved ? <p className="sub">{saved}</p> : null}

			{tab === "leave" ? (
				<LeaveSettingsSection
					rows={leavePolicy}
					disabled={!canManage || pending}
					onChange={setLeavePolicy}
					onSave={() => save({ leavePolicy })}
				/>
			) : null}

			{tab === "attendance" ? (
				<AttendanceSettingsSection
					lateMinutes={lateMinutes}
					shifts={shifts}
					slots={attendanceSlots}
					disabled={!canManage || pending}
					onLateMinutes={setLateMinutes}
					onShifts={setShifts}
					onSlots={setAttendanceSlots}
					onSave={() => {
						const storedShifts = shiftsWithIds(shifts);
						save({
							attendanceSettings: { lateMinutes, shifts: storedShifts },
							attendanceSlots,
						});
					}}
				/>
			) : null}

			{tab === "claims" ? (
				<ClaimsSettingsSection
					rows={claimPolicy}
					disabled={!canManage || pending}
					onChange={setClaimPolicy}
					onSave={() => save({ claimPolicy })}
				/>
			) : null}

			{tab === "payroll" ? (
				<PayrollSettingsSection
					payDay={payDay}
					epf={epf}
					socso={socso}
					eis={eis}
					disabled={!canManage || pending}
					onPayDay={setPayDay}
					onEpf={setEpf}
					onSocso={setSocso}
					onEis={setEis}
					onSave={() => save({ payrollSettings: { payDay, epf, socso, eis } })}
				/>
			) : null}

			<p className="sub">
				Open the module for day-to-day work:{" "}
				<Link to="/hub/leave">Leave</Link>, <Link to="/hub/attendance">Attendance</Link>, <Link to="/hub/claims">Claims</Link>,{" "}
				<Link to="/hub/payroll">Payroll</Link>.
			</p>
		</div>
	);
}

function LeaveSettingsSection({
	rows,
	disabled,
	onChange,
	onSave,
}: {
	rows: LeavePolicy[];
	disabled: boolean;
	onChange: (rows: LeavePolicy[]) => void;
	onSave: () => void;
}) {
	const [draft, setDraft] = useState<LeavePolicy>({ kind: "", days: 12, paid: true, approval: true });
	const [editIndex, setEditIndex] = useState<number | null>(null);

	function resetDraft() {
		setDraft({ kind: "", days: 12, paid: true, approval: true });
		setEditIndex(null);
	}

	function submit(event: FormEvent) {
		event.preventDefault();
		const kind = slugKind(draft.kind);
		if (!kind) return;
		const next = [...rows];
		const record = { ...draft, kind };
		if (editIndex != null) next[editIndex] = record;
		else next.push(record);
		onChange(next);
		resetDraft();
	}

	return (
		<div className="panel">
			<SectionBar sub="Leave types, entitlements and approval rules.">
				<button className="btn" type="button" disabled={disabled} onClick={onSave}>
					Save leave settings
				</button>
			</SectionBar>
			{rows.length === 0 ? <p className="empty">No leave types yet.</p> : (
				<div className="table-wrap">
					<table>
						<thead>
							<tr>
								<th>Type</th>
								<th>Days</th>
								<th>Paid</th>
								<th>Needs approval</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{rows.map((row, index) => (
								<tr key={row.kind}>
									<td>{label(row.kind)}</td>
									<td>{row.days}</td>
									<td>{row.paid ? "Yes" : "No"}</td>
									<td>{row.approval ? "Yes" : "No"}</td>
									<td>
										<button
											type="button"
											className="btn ghost"
											disabled={disabled}
											onClick={() => {
												setEditIndex(index);
												setDraft({ ...row });
											}}
										>
											Edit
										</button>
										<button
											type="button"
											className="btn ghost"
											disabled={disabled}
											onClick={() => onChange(rows.filter((_, i) => i !== index))}
										>
											Remove
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<form className="grid two" onSubmit={submit}>
				<div>
					<label htmlFor="lv-kind-code">Type code</label>
					<input
						id="lv-kind-code"
						value={draft.kind}
						disabled={disabled || editIndex != null}
						onChange={(event) => setDraft({ ...draft, kind: event.target.value })}
						placeholder="annual"
						required
					/>
				</div>
				<div>
					<label htmlFor="lv-days">Days per year</label>
					<input
						id="lv-days"
						type="number"
						min={0}
						max={366}
						value={draft.days}
						disabled={disabled}
						onChange={(event) => setDraft({ ...draft, days: Number(event.target.value) || 0 })}
						required
					/>
				</div>
				<ToggleRow label="Paid leave" checked={draft.paid} disabled={disabled} onChange={(paid) => setDraft({ ...draft, paid })} />
				<ToggleRow label="Needs approval" checked={draft.approval} disabled={disabled} onChange={(approval) => setDraft({ ...draft, approval })} />
				<p className="actions">
					<button className="btn" type="submit" disabled={disabled}>
						{editIndex != null ? "Update type" : "Add type"}
					</button>
					{editIndex != null ? (
						<button className="btn ghost" type="button" disabled={disabled} onClick={resetDraft}>
							Cancel edit
						</button>
					) : null}
				</p>
			</form>
		</div>
	);
}

function ClaimsSettingsSection({
	rows,
	disabled,
	onChange,
	onSave,
}: {
	rows: ClaimPolicy[];
	disabled: boolean;
	onChange: (rows: ClaimPolicy[]) => void;
	onSave: () => void;
}) {
	const [draft, setDraft] = useState<ClaimPolicy>({ kind: "", limit: "0", approval: true });
	const [editIndex, setEditIndex] = useState<number | null>(null);

	function resetDraft() {
		setDraft({ kind: "", limit: "0", approval: true });
		setEditIndex(null);
	}

	function submit(event: FormEvent) {
		event.preventDefault();
		const kind = slugKind(draft.kind);
		if (!kind) return;
		const next = [...rows];
		const record = { ...draft, kind };
		if (editIndex != null) next[editIndex] = record;
		else next.push(record);
		onChange(next);
		resetDraft();
	}

	return (
		<div className="panel">
			<SectionBar sub="Claim types, limits and approval rules.">
				<button className="btn" type="button" disabled={disabled} onClick={onSave}>
					Save claims settings
				</button>
			</SectionBar>
			{rows.length === 0 ? <p className="empty">No claim types yet.</p> : (
				<div className="table-wrap">
					<table>
						<thead>
							<tr>
								<th>Type</th>
								<th>Limit (RM)</th>
								<th>Needs approval</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{rows.map((row, index) => (
								<tr key={row.kind}>
									<td>{label(row.kind)}</td>
									<td>{row.limit}</td>
									<td>{row.approval ? "Yes" : "No"}</td>
									<td>
										<button
											type="button"
											className="btn ghost"
											disabled={disabled}
											onClick={() => {
												setEditIndex(index);
												setDraft({ ...row });
											}}
										>
											Edit
										</button>
										<button
											type="button"
											className="btn ghost"
											disabled={disabled}
											onClick={() => onChange(rows.filter((_, i) => i !== index))}
										>
											Remove
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<form className="grid two" onSubmit={submit}>
				<div>
					<label htmlFor="cl-kind-code">Type code</label>
					<input
						id="cl-kind-code"
						value={draft.kind}
						disabled={disabled || editIndex != null}
						onChange={(event) => setDraft({ ...draft, kind: event.target.value })}
						placeholder="fuel"
						required
					/>
				</div>
				<div>
					<label htmlFor="cl-limit">Limit (RM)</label>
					<input
						id="cl-limit"
						inputMode="decimal"
						value={draft.limit}
						disabled={disabled}
						onChange={(event) => setDraft({ ...draft, limit: event.target.value })}
						required
					/>
				</div>
				<ToggleRow label="Needs approval" checked={draft.approval} disabled={disabled} onChange={(approval) => setDraft({ ...draft, approval })} />
				<p className="actions">
					<button className="btn" type="submit" disabled={disabled}>
						{editIndex != null ? "Update type" : "Add type"}
					</button>
					{editIndex != null ? (
						<button className="btn ghost" type="button" disabled={disabled} onClick={resetDraft}>
							Cancel edit
						</button>
					) : null}
				</p>
			</form>
		</div>
	);
}

function AttendanceSettingsSection({
	lateMinutes,
	shifts,
	slots,
	disabled,
	onLateMinutes,
	onShifts,
	onSlots,
	onSave,
}: {
	lateMinutes: number;
	shifts: ShiftWithId[];
	slots: AttendanceSlot[];
	disabled: boolean;
	onLateMinutes: (value: number) => void;
	onShifts: (rows: ShiftWithId[]) => void;
	onSlots: (rows: AttendanceSlot[]) => void;
	onSave: () => void;
}) {
	const [shiftDraft, setShiftDraft] = useState<Shift>({ name: "", start: "09:00", end: "18:00" });
	const [shiftEdit, setShiftEdit] = useState<number | null>(null);
	const [slotDraft, setSlotDraft] = useState<AttendanceSlot>({ person: "", days: ["mon", "tue", "wed", "thu", "fri"], shift: "day" });

	function addShift(event: FormEvent) {
		event.preventDefault();
		if (!shiftDraft.name.trim()) return;
		const id = shiftEdit != null ? shifts[shiftEdit]?.id ?? shiftId(shiftDraft.name, shifts) : shiftId(shiftDraft.name, shifts);
		const row = { ...shiftDraft, id };
		const next = [...shifts];
		if (shiftEdit != null) next[shiftEdit] = row;
		else next.push(row);
		onShifts(next);
		setShiftDraft({ name: "", start: "09:00", end: "18:00" });
		setShiftEdit(null);
	}

	function addSlot(event: FormEvent) {
		event.preventDefault();
		if (!slotDraft.person.trim()) return;
		onSlots([...slots, { ...slotDraft, mine: false }]);
		setSlotDraft({ person: "", days: ["mon", "tue", "wed", "thu", "fri"], shift: slotDraft.shift || "day" });
	}

	return (
		<div className="panel">
			<SectionBar sub="Late threshold, shift times and weekly schedules.">
				<button className="btn" type="button" disabled={disabled} onClick={onSave}>
					Save attendance settings
				</button>
			</SectionBar>
			<div className="grid two">
				<div>
					<label htmlFor="at-late">Mark late after (minutes)</label>
					<input
						id="at-late"
						type="number"
						min={0}
						max={480}
						value={lateMinutes}
						disabled={disabled}
						onChange={(event) => onLateMinutes(Number(event.target.value) || 0)}
					/>
				</div>
			</div>
			<h3>Shifts</h3>
			{shifts.length === 0 ? <p className="empty">No shifts yet.</p> : (
				<div className="table-wrap">
					<table>
						<thead>
							<tr>
								<th>Name</th>
								<th>Start</th>
								<th>End</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{shifts.map((row, index) => (
								<tr key={row.id ?? row.name}>
									<td>{row.name}</td>
									<td>{row.start}</td>
									<td>{row.end}</td>
									<td>
										<button
											type="button"
											className="btn ghost"
											disabled={disabled}
											onClick={() => {
												setShiftEdit(index);
												setShiftDraft({ ...row });
											}}
										>
											Edit
										</button>
										<button type="button" className="btn ghost" disabled={disabled} onClick={() => onShifts(shifts.filter((_, i) => i !== index))}>
											Remove
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<form className="grid two" onSubmit={addShift}>
				<div>
					<label htmlFor="sh-name">Shift name</label>
					<input id="sh-name" value={shiftDraft.name} disabled={disabled} onChange={(event) => setShiftDraft({ ...shiftDraft, name: event.target.value })} required />
				</div>
				<div>
					<label htmlFor="sh-start">Start</label>
					<input id="sh-start" value={shiftDraft.start} disabled={disabled} onChange={(event) => setShiftDraft({ ...shiftDraft, start: event.target.value })} />
				</div>
				<div>
					<label htmlFor="sh-end">End</label>
					<input id="sh-end" value={shiftDraft.end} disabled={disabled} onChange={(event) => setShiftDraft({ ...shiftDraft, end: event.target.value })} />
				</div>
				<p className="actions">
					<button className="btn" type="submit" disabled={disabled}>
						{shiftEdit != null ? "Update shift" : "Add shift"}
					</button>
				</p>
			</form>

			<h3>Weekly shift schedule</h3>
			{slots.length === 0 ? <p className="empty">No shift schedule yet.</p> : (
				<div className="table-wrap">
					<table>
						<thead>
							<tr>
								<th>Person</th>
								<th>Days</th>
								<th>Shift</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{slots.map((row, index) => (
								<tr key={row.id ?? `${row.person}-${index}`}>
									<td>{row.person}</td>
									<td>{row.days.map((day) => label(day)).join(", ")}</td>
									<td>{label(row.shift)}</td>
									<td>
										<button type="button" className="btn ghost" disabled={disabled} onClick={() => onSlots(slots.filter((_, i) => i !== index))}>
											Remove
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<form className="grid two" onSubmit={addSlot}>
				<div>
					<label htmlFor="sl-person">Person</label>
					<input id="sl-person" value={slotDraft.person} disabled={disabled} onChange={(event) => setSlotDraft({ ...slotDraft, person: event.target.value })} required />
				</div>
				<div>
					<label htmlFor="sl-shift">Shift</label>
					<Select id="sl-shift" name="shift" value={slotDraft.shift} disabled={disabled} onChange={(event) => setSlotDraft({ ...slotDraft, shift: event.target.value })}>
						{(shifts.length ? shifts : [{ id: "day", name: "Day", start: "", end: "" }]).map((row) => (
							<option key={row.id ?? row.name} value={row.id ?? slugKind(row.name)}>
								{row.name}
							</option>
						))}
					</Select>
				</div>
				<fieldset className="grid two">
					<legend>Working days</legend>
					{WEEKDAYS.map(([value, text]) => (
						<label key={value} className="row gap">
							<input
								type="checkbox"
								checked={slotDraft.days.includes(value)}
								disabled={disabled}
								onChange={(event) => {
									const days = event.target.checked
										? [...slotDraft.days, value]
										: slotDraft.days.filter((day) => day !== value);
									setSlotDraft({ ...slotDraft, days });
								}}
							/>
							<span>{text}</span>
						</label>
					))}
				</fieldset>
				<p className="actions">
					<button className="btn" type="submit" disabled={disabled}>
						Add schedule row
					</button>
				</p>
			</form>
		</div>
	);
}

function PayrollSettingsSection({
	payDay,
	epf,
	socso,
	eis,
	disabled,
	onPayDay,
	onEpf,
	onSocso,
	onEis,
	onSave,
}: {
	payDay: number;
	epf: number;
	socso: string;
	eis: string;
	disabled: boolean;
	onPayDay: (value: number) => void;
	onEpf: (value: number) => void;
	onSocso: (value: string) => void;
	onEis: (value: string) => void;
	onSave: () => void;
}) {
	return (
		<div className="panel grid two">
			<SectionBar sub="Pay day and statutory amounts applied to payroll runs.">
				<button className="btn" type="button" disabled={disabled} onClick={onSave}>
					Save payroll settings
				</button>
			</SectionBar>
			<div>
				<label htmlFor="pr-day">Pay day of month</label>
				<input id="pr-day" type="number" min={1} max={31} value={payDay || ""} disabled={disabled} onChange={(event) => onPayDay(Number(event.target.value) || 0)} />
			</div>
			<div>
				<label htmlFor="pr-epf">EPF employee (%)</label>
				<input id="pr-epf" type="number" min={0} max={100} step="0.01" value={epf || ""} disabled={disabled} onChange={(event) => onEpf(Number(event.target.value) || 0)} />
			</div>
			<div>
				<label htmlFor="pr-socso">SOCSO (RM)</label>
				<input id="pr-socso" inputMode="decimal" value={socso} disabled={disabled} onChange={(event) => onSocso(event.target.value)} />
			</div>
			<div>
				<label htmlFor="pr-eis">EIS (RM)</label>
				<input id="pr-eis" inputMode="decimal" value={eis} disabled={disabled} onChange={(event) => onEis(event.target.value)} />
			</div>
		</div>
	);
}
